const EMAIL_PATTERN = /^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/;
const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

export function normalizeEmail(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.toLowerCase();
}

export function normalizePhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) {
    return null;
  }
  if (trimmed.startsWith("+")) {
    return `+${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("8")) {
    return `+7${digits.slice(1)}`;
  }
  if (digits.length === 11 && digits.startsWith("7")) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+7${digits}`;
  }
  return `+${digits}`;
}

export function isValidEmail(raw: string): boolean {
  const normalized = normalizeEmail(raw);
  return normalized !== null && EMAIL_PATTERN.test(normalized);
}

export function isValidPhone(raw: string): boolean {
  const normalized = normalizePhone(raw);
  return normalized !== null && PHONE_PATTERN.test(normalized);
}

export function hasCompleteReceiptContact(
  email?: string | null,
  phoneNumber?: string | null,
): boolean {
  return Boolean(email?.trim() && phoneNumber?.trim());
}

export function formatReceiptDestination(
  email?: string | null,
  phoneNumber?: string | null,
): string | null {
  const normalizedEmail = email?.trim();
  const normalizedPhone = phoneNumber?.trim();
  if (normalizedEmail && normalizedPhone) {
    return `Чек будет отправлен на ${normalizedEmail} и ${normalizedPhone}`;
  }
  if (normalizedEmail) {
    return `Для чека добавьте телефон. Email уже указан: ${normalizedEmail}`;
  }
  if (normalizedPhone) {
    return `Для чека добавьте email. Телефон уже указан: ${normalizedPhone}`;
  }
  return null;
}

export type CheckoutContactValidationError =
  | "missing"
  | "missing_email"
  | "missing_phone"
  | "email"
  | "phone";

export function validateCheckoutContact(
  storedEmail?: string | null,
  storedPhone?: string | null,
  draftEmail?: string,
  draftPhone?: string,
):
  | { ok: true; contact: { customer_email?: string; customer_phone?: string } }
  | { ok: false; error: CheckoutContactValidationError } {
  const draftEmailValue = draftEmail?.trim() ?? "";
  const draftPhoneValue = draftPhone?.trim() ?? "";
  const emailRaw = draftEmailValue || storedEmail?.trim() || "";
  const phoneRaw = draftPhoneValue || storedPhone?.trim() || "";

  if (!emailRaw && !phoneRaw) {
    return { ok: false, error: "missing" };
  }
  if (!emailRaw) {
    return { ok: false, error: "missing_email" };
  }
  if (!phoneRaw) {
    return { ok: false, error: "missing_phone" };
  }
  if (emailRaw && !isValidEmail(emailRaw)) {
    return { ok: false, error: "email" };
  }
  if (phoneRaw && !isValidPhone(phoneRaw)) {
    return { ok: false, error: "phone" };
  }

  const email = emailRaw ? normalizeEmail(emailRaw) : null;
  const phone = phoneRaw ? normalizePhone(phoneRaw) : null;
  return {
    ok: true,
    contact: {
      ...(email ? { customer_email: email } : {}),
      ...(phone ? { customer_phone: phone } : {}),
    },
  };
}

export function buildCheckoutContact(
  storedEmail?: string | null,
  storedPhone?: string | null,
  draftEmail?: string,
  draftPhone?: string,
): { customer_email?: string; customer_phone?: string } {
  const result = validateCheckoutContact(
    storedEmail,
    storedPhone,
    draftEmail,
    draftPhone,
  );
  if (!result.ok) {
    return {};
  }
  return result.contact;
}
